// Como a paciente aparece no alto de cada papel.
//
// Pedido do Ilton (27/09): no que vai para a equipe — relatório do médico,
// exames, o especial para entregar —, o nome completo, a data de nascimento,
// o prontuário e o atendimento, como consta na documentação do hospital.
// "Dona Maria" fica para os papéis da família.
//
// Os valores vêm do banco; aqui só se decide o que mostrar. Sem React, para o
// PDF e o teste usarem o mesmo.
import { formatarData } from '@medicov1/shared';

export interface PacienteNoPapel {
  apelido: string;
  idade: number;
  quarto: string | null;
  internadoDesde: string;
  nomeCompleto?: string | null;
  /** "AAAA-MM-DD". */
  nascimentoEm?: string | null;
  registro?: string | null;
  atendimento?: string | null;
}

const MINUSCULAS = new Set(['da', 'das', 'de', 'do', 'dos', 'e']);

/**
 * "ANA DA SILVA E SOUZA" -> "Ana da Silva e Souza". O sistema do
 * hospital grava em caixa alta; no papel, caixa alta inteira grita. Nome já
 * escrito com minúsculas fica como está.
 */
export function nomeProprio(nome: string): string {
  const limpo = nome.trim().replace(/\s+/g, ' ');
  if (limpo !== limpo.toUpperCase()) return limpo;
  return limpo.toLowerCase().split(' ')
    .map((p, i) => (i > 0 && MINUSCULAS.has(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(' ');
}

/**
 * Cada pedaço da linha ("internada desde 30/07/2026") não se parte: os espaços
 * de dentro são inquebráveis. Pedido do Ilton (05/10): quando a linha quebrava
 * entre "desde" e a data, o "30/07/2026" ficava sozinho embaixo e parecia a
 * data do relatório.
 */
const inteiro = (parte: string) => parte.replace(/ /g, ' ');

/**
 * O nome e a linha de baixo. Para a equipe, só troca se houver nome de
 * registro: sem ele, o apelido é melhor que nada.
 */
export function identificar(p: PacienteNoPapel, paraEquipe: boolean): { nome: string; linha: string } {
  const quem = paraEquipe && p.nomeCompleto;
  if (!quem) {
    return {
      nome: p.apelido,
      linha: [`${p.idade} anos`, p.quarto && `quarto ${p.quarto}`, `internada desde ${formatarData(p.internadoDesde)}`]
        .filter((x): x is string => Boolean(x)).map(inteiro).join(' · '),
    };
  }
  return {
    nome: nomeProprio(p.nomeCompleto!),
    linha: [
      p.nascimentoEm ? `nascimento ${formatarData(p.nascimentoEm)} (${p.idade} anos)` : `${p.idade} anos`,
      p.registro && `prontuário ${p.registro}`,
      p.atendimento && `atendimento ${p.atendimento}`,
      p.quarto && `leito ${p.quarto}`,
      `internada desde ${formatarData(p.internadoDesde)}`,
    ].filter((x): x is string => Boolean(x)).map(inteiro).join(' · '),
  };
}
